import html2canvas from "html2canvas";
import jsPdf from "jspdf";
import { ArrowLeft, FileText } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { QuoteList } from "@/components/QuoteList";
import { QuoteViewer } from "@/components/QuoteViewer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { cloudflare } from "@/integrations/cloudflare/client";

interface Project {
  id: string;
  project_name?: string;
  customer_id?: number;
  customer_name?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  budget?: string;
  timeframe?: string;
  description?: string;
  status: string;
  total_quote_value: number;
  created_at: string;
  updated_at: string;
}

interface Quote {
  id: string;
  project_id: string;
  quote_name: string;
  quote_number: string;
  status: "draft" | "sent" | "accepted" | "rejected" | "expired";
  notes?: string;
  total_amount: number;
  created_at: string;
  updated_at: string;
}

interface ProjectDetailsProps {
  projectId: string;
  onBack: () => void;
}

export function ProjectDetails({ projectId, onBack }: ProjectDetailsProps) {
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedQuote, setSelectedQuote] = useState<Quote | null>(null);
  const [showQuoteViewer, setShowQuoteViewer] = useState(false);
  const quotationRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  const loadProjectData = useCallback(async () => {
    try {
      setLoading(true);

      // Load project details using cloudflare client
      const { data: projectData, error: projectError } = await cloudflare
        .from("projects")
        .select("*")
        .eq("id", projectId)
        .single();

      if (projectError) {
        throw projectError;
      }
      setProject(projectData);
    } catch (_error) {
      toast({
        title: "Failed to Load Project",
        description: "Unable to load project data. Click to retry or go back.",
        variant: "destructive",
        action: (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => loadProjectData()}>
              Retry
            </Button>
            <Button variant="outline" size="sm" onClick={onBack}>
              Go Back
            </Button>
          </div>
        ),
      });
    } finally {
      setLoading(false);
    }
  }, [projectId, toast, onBack]);

  useEffect(() => {
    loadProjectData();
  }, [loadProjectData]);

  // Print functionality
  const handlePrint = useReactToPrint({
    content: () => quotationRef.current,
    documentTitle: `Quote-${project?.customer_name}-${new Date().toISOString().split("T")[0]}`,
  });

  // PDF download functionality
  const _handleDownloadPdf = async () => {
    if (!quotationRef.current) {
      toast({
        title: "Unable to Generate PDF",
        description: "Quote content is not available for PDF generation.",
        variant: "destructive",
      });
      return;
    }

    let canvas: HTMLCanvasElement | null = null;

    try {
      toast({
        title: "Generating PDF",
        description: "Please wait while we generate your PDF...",
      });

      canvas = await html2canvas(quotationRef.current, {
        scale: 1.5, // Reduced scale to prevent memory issues
        useCORS: true,
        logging: false,
        allowTaint: false,
        timeout: 30000, // 30 second timeout
      });

      const imgData = canvas.toDataURL("image/png", 0.8); // Reduced quality to save memory
      const pdf = new jsPdf({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight) * 0.95;
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = 10;

      pdf.addImage(imgData, "PNG", imgX, imgY, imgWidth * ratio, imgHeight * ratio);
      pdf.save(`Quote-${project?.customer_name}-${new Date().toISOString().split("T")[0]}.pdf`);

      toast({
        title: "PDF Downloaded",
        description: "Your quotation has been downloaded successfully.",
      });
    } catch (_error) {
      toast({
        title: "PDF Generation Failed",
        description: "Unable to generate PDF. Try printing instead.",
        variant: "destructive",
        action: (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => _handleDownloadPdf()}>
              Retry PDF
            </Button>
            <Button variant="outline" size="sm" onClick={handlePrint}>
              Print Instead
            </Button>
          </div>
        ),
      });
    } finally {
      // Cleanup canvas to prevent memory leaks
      if (canvas) {
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
        canvas.width = 0;
        canvas.height = 0;
      }
    }
  };

  // Generate Quote handler (existing functionality)
  const handleGenerateQuote = () => {
    navigate(`/quote-builder/${projectId}`);
  };

  // Quote management handlers
  const handleViewQuote = (quote: Quote) => {
    setSelectedQuote(quote);
    setShowQuoteViewer(true);
  };

  const handleEditQuote = (quote: Quote) => {
    navigate(`/quote-builder/${projectId}?quoteId=${quote.id}`);
  };

  const handleGenerateQuoteFromQuote = (quote: Quote) => {
    setSelectedQuote(quote);
    setShowQuoteViewer(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading project details...</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Project not found.</p>
        <Button onClick={onBack} className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Go Back
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={onBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              {project?.project_name ||
                project?.customer_name ||
                `${project?.first_name || ""} ${project?.last_name || ""} - Project`.trim()}
            </h1>
            <p className="text-muted-foreground">
              Project ID: {project?.id} • {project?.city || "Location not specified"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge
            variant={
              project.status === "new"
                ? "default"
                : project.status === "draft"
                  ? "secondary"
                  : "outline"
            }
            className="text-xs"
          >
            {project.status.toUpperCase()}
          </Badge>
        </div>
      </div>

      {/* Project Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Project Information</span>
            <div className="text-2xl font-bold text-primary">
              ${(project?.total_quote_value || 0).toFixed(2)}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-sm font-medium text-muted-foreground">Customer</Label>
            <p className="text-foreground">{project.customer_name}</p>
          </div>
          <div>
            <Label className="text-sm font-medium text-muted-foreground">Email</Label>
            <p className="text-foreground">{project.email || "N/A"}</p>
          </div>
          <div>
            <Label className="text-sm font-medium text-muted-foreground">Phone</Label>
            <p className="text-foreground">{project.phone || "N/A"}</p>
          </div>
          <div>
            <Label className="text-sm font-medium text-muted-foreground">Address</Label>
            <p className="text-foreground">{project.address || "N/A"}</p>
          </div>
          <div>
            <Label className="text-sm font-medium text-muted-foreground">Budget</Label>
            <p className="text-foreground">{project.budget || "N/A"}</p>
          </div>
          <div>
            <Label className="text-sm font-medium text-muted-foreground">Timeframe</Label>
            <p className="text-foreground">{project.timeframe || "N/A"}</p>
          </div>
        </CardContent>
      </Card>

      {/* Quote Management Section */}
      <QuoteList
        projectId={projectId}
        onViewQuote={handleViewQuote}
        onEditQuote={handleEditQuote}
        onGenerateQuote={handleGenerateQuoteFromQuote}
      />

      {/* Actions */}
      <div className="flex justify-end">
        <Button size="lg" className="px-8" onClick={handleGenerateQuote}>
          <FileText className="w-4 h-4 mr-2" />
          Generate Quote
        </Button>
      </div>

      {/* Quote Viewer */}
      <QuoteViewer
        quote={selectedQuote}
        project={project}
        open={showQuoteViewer}
        onOpenChange={setShowQuoteViewer}
      />
    </div>
  );
}
